import { TFunction } from '@getpopsure/i18n-react';
import { calendly, insurance } from '@getpopsure/private-constants';
import { getAgeFromBirthYear } from '@getpopsure/private-health-insurance-pricing-engine';
import { englishFormattedEuroCurrency } from '@getpopsure/public-utility';

import { productLinks } from '../../../constants/internalLinks';
import {
  CalculationResultsTextData,
  ContentVariant,
  GENERAL_NON_EMPLOYEE_INCOME_THRESHOLD,
  HALLESCHE_FREELANCER_INCOME_THRESHOLD,
  HALLESCHE_OTHER_INCOME_THRESHOLD,
  PRIVATE_HEALTH_INCOME_TRESHOLD,
  YEARLY_MINIJOB_THRESHOLD,
} from './model';

const birthYearToAgeGroupStr = (birthYear: number) => {
  const age = getAgeFromBirthYear(birthYear);
  if (age < 25) {
    return encodeURIComponent('Under 25');
  }
  if (age > 35) {
    return encodeURIComponent('Under 35');
  }

  return String(age);
};

export const getTranslatedTextData = ({
  t,
  birthYear,
}: {
  t: TFunction;
  birthYear: number;
}): Record<ContentVariant, CalculationResultsTextData> => ({
  NO_PRICE: {},
  TOO_OLD: {
    infoTitle: t(
      'privatehealth:calculator.results.tooOld.title',
      'We cannot sign you up if you’re older than 90'
    ),
  },
  'SPOUSE--TOO_OLD': {
    infoTitle: t(
      'privatehealth:calculator.results.tooOld.title.spouse',
      'We cannot sign them up if they’re older than 90'
    ),
  },

  'OTHER--HANSE_MERKUR_REDIRECT': {
    infoTitle: t(
      'privatehealth:calculator.results.otherOccupation.inHanseMerkurRange.title',
      'Your tariff might be different'
    ),
    infoDetails: t(
      'privatehealth:calculator.results.otherOccupation.inHanseMerkurRange.details',
      {
        defaultValue:
          "For most other occupations, if you're earning under {{incomeThresholdStr}}, you could be [eligible for a different tariff]({{tallyLink}})",
        incomeThresholdStr: englishFormattedEuroCurrency(
          HALLESCHE_OTHER_INCOME_THRESHOLD,
          true
        ),
        tallyLink: `https://tally.so/r/mDzpBZ?ageGroup=${birthYearToAgeGroupStr(
          birthYear
        )}`,
      }
    ),
  },
  'SPOUSE--OTHER--HANSE_MERKUR_REDIRECT': {
    infoTitle: t(
      'privatehealth:calculator.results.otherOccupation.inHanseMerkurRange.title.spouse',
      'Their tariff might be different'
    ),
    infoDetails: t(
      'privatehealth:calculator.results.otherOccupation.inHanseMerkurRange.details.spouse',
      {
        defaultValue:
          "For most other occupations, if they're earning under {{incomeThresholdStr}}, they could be [eligible for a different tariff]({{tallyLink}})",
        incomeThresholdStr: englishFormattedEuroCurrency(
          HALLESCHE_OTHER_INCOME_THRESHOLD,
          true
        ),
        tallyLink: `https://tally.so/r/mDzpBZ?ageGroup=${birthYearToAgeGroupStr(
          birthYear
        )}`,
      }
    ),
  },

  'SELF_EMPLOYED--HANSE_MERKUR_REDIRECT': {
    infoTitle: t(
      'privatehealth:calculator.results.selfEmployed.inHanseMerkurRange.title',
      'Your tariff might be different'
    ),
    infoDetails: t(
      'privatehealth:calculator.results.selfEmployed.inHanseMerkurRange.details',
      {
        defaultValue:
          'As a freelancer earning under {{incomeThresholdStr}}, you could be [eligible for a different tariff]({{tallyLink}})',
        incomeThresholdStr: englishFormattedEuroCurrency(
          HALLESCHE_FREELANCER_INCOME_THRESHOLD,
          true
        ),
        tallyLink: `https://tally.so/r/mDzpBZ?ageGroup=${birthYearToAgeGroupStr(
          birthYear
        )}`,
      }
    ),
  },
  'SPOUSE--SELF_EMPLOYED--HANSE_MERKUR_REDIRECT': {
    infoTitle: t(
      'privatehealth:calculator.results.selfEmployed.inHanseMerkurRange.title.spouse',
      'Their tariff might be different'
    ),
    infoDetails: t(
      'privatehealth:calculator.results.selfEmployed.inHanseMerkurRange.details.spouse',
      {
        defaultValue:
          'As a freelancer earning under {{incomeThresholdStr}}, they could be [eligible for a different tariff]({{tallyLink}})',
        incomeThresholdStr: englishFormattedEuroCurrency(
          HALLESCHE_FREELANCER_INCOME_THRESHOLD,
          true
        ),
        tallyLink: `https://tally.so/r/mDzpBZ?ageGroup=${birthYearToAgeGroupStr(
          birthYear
        )}`,
      }
    ),
  },

  'EMPLOYED_IN_GERMANY--INCOME_TOO_LOW': {
    infoTitle: t(
      'privatehealth:calculator.results.employed.underThreshold.title',
      "You're not eligible"
    ),
    infoDetails: t(
      'privatehealth:calculator.results.employed.underThreshold.details',
      {
        defaultValue:
          'As an employed person earning less than {{privateIncomeThresholdStr}} per year, you have to be on [public health insurance]({{publicHealthLink}}).',
        privateIncomeThresholdStr: englishFormattedEuroCurrency(
          PRIVATE_HEALTH_INCOME_TRESHOLD,
          true
        ),
        publicHealthLink: insurance.publicHealth.landingPage,
      }
    ),
  },
  'SPOUSE--EMPLOYED_IN_GERMANY--INCOME_TOO_LOW': {
    infoTitle: t(
      'privatehealth:calculator.results.employed.underThreshold.title.spouse',
      "They're not eligible"
    ),
    infoDetails: t(
      'privatehealth:calculator.results.employed.underThreshold.details.spouse',
      {
        defaultValue:
          'As an employed person earning between {{miniJobThreshold}} and {{privateIncomeThresholdStr}} per year, they have to be on [public health insurance]({{publicHealthLink}}).',
        privateIncomeThresholdStr: englishFormattedEuroCurrency(
          PRIVATE_HEALTH_INCOME_TRESHOLD,
          true
        ),
        miniJobThreshold: englishFormattedEuroCurrency(
          YEARLY_MINIJOB_THRESHOLD,
          true
        ),
        publicHealthLink: insurance.publicHealth.landingPage,
      }
    ),
  },

  'OTHER--INCOME_TOO_LOW': {
    infoTitle: t(
      'privatehealth:calculator.results.otherOccupation.underThreshold.title',
      'You might not be eligible'
    ),
    infoDetails: t(
      'privatehealth:calculator.results.otherOccupation.underThreshold.details',
      {
        defaultValue:
          'You need to earn at least €25,000 per year to sign up for private health. However, you are eligible for our [expat health insurance]({{expatHealthInsuranceLink}}).',
        generalNonEmployeeTreshold: englishFormattedEuroCurrency(
          GENERAL_NON_EMPLOYEE_INCOME_THRESHOLD,
          true
        ),
        expatHealthInsuranceLink: productLinks.EXPAT_HEALTH,
      }
    ),
  },
  'SPOUSE--OTHER--INCOME_TOO_LOW': {
    infoTitle: t(
      'privatehealth:calculator.results.otherOccupation.underThreshold.title.spouse',
      'They might not be eligible'
    ),
    infoDetails: t(
      'privatehealth:calculator.results.otherOccupation.underThreshold.details.spouse',
      {
        defaultValue:
          'Since they’re earning above {{miniJobThreshold}}, they can’t be a dependent and need to earn at least {{generalNonEmployeeTreshold}} to be eligible.',
        miniJobThreshold: englishFormattedEuroCurrency(
          YEARLY_MINIJOB_THRESHOLD,
          true
        ),
        generalNonEmployeeTreshold: englishFormattedEuroCurrency(
          GENERAL_NON_EMPLOYEE_INCOME_THRESHOLD,
          true
        ),
        talkToUsLink: calendly.healthInsuranceAdvice,
      }
    ),
  },

  'SELF_EMPLOYED--INCOME_TOO_LOW': {
    infoTitle: t(
      'privatehealth:calculator.results.selfEmployed.underThreshold.title',
      'You might not be eligible'
    ),
    infoDetails: t(
      'privatehealth:calculator.results.selfEmployed.underThreshold.details',
      {
        defaultValue:
          'As a freelancer, you need to earn at least {{incomeThresholdStr}} per year to sign up for private health. However, you are eligible for our [expat health insurance]({{expatHealthInsuranceLink}}).',
        incomeThresholdStr: englishFormattedEuroCurrency(
          GENERAL_NON_EMPLOYEE_INCOME_THRESHOLD,
          true
        ),
        expatHealthInsuranceLink: productLinks.EXPAT_HEALTH,
      }
    ),
  },
  'SPOUSE--SELF_EMPLOYED--INCOME_TOO_LOW': {
    infoTitle: t(
      'privatehealth:calculator.results.selfEmployed.underThreshold.title.spouse',
      'They might not be eligible'
    ),
    infoDetails: t(
      'privatehealth:calculator.results.selfEmployed.underThreshold.details.spouse',
      {
        defaultValue:
          'Since they’re earning above {{miniJobThreshold}}, they can’t be a dependent and need to earn at least {{generalNonEmployeeTreshold}} to be eligible.',
        miniJobThreshold: englishFormattedEuroCurrency(
          YEARLY_MINIJOB_THRESHOLD,
          true
        ),
        generalNonEmployeeTreshold: englishFormattedEuroCurrency(
          GENERAL_NON_EMPLOYEE_INCOME_THRESHOLD,
          true
        ),
      }
    ),
  },

  'OTHER--INCOME_TOO_LOW_BUT_TOO_OLD_FOR_HANSE_MERKUR': {
    infoTitle: t(
      'privatehealth:calculator.results.otherOccupation.isOverHanseMerkurAgeThreshold.title',
      'You might not be eligible'
    ),
    infoDetails: t(
      'privatehealth:calculator.results.otherOccupation.isOverHanseMerkurAgeThreshold.details',
      {
        defaultValue:
          'For most other occupations, you need to earn at least {{hallescheOtherThreshold}} to be eligible. [Talk to us]({{talkToUsLink}}) to know your options.',
        hallescheOtherThreshold: englishFormattedEuroCurrency(
          HALLESCHE_OTHER_INCOME_THRESHOLD,
          true
        ),
        talkToUsLink: calendly.healthInsuranceAdvice,
      }
    ),
  },
  'SPOUSE--OTHER--INCOME_TOO_LOW_BUT_TOO_OLD_FOR_HANSE_MERKUR': {
    infoTitle: t(
      'privatehealth:calculator.results.otherOccupation.isOverHanseMerkurAgeThreshold.title.spouse',
      'They might not be eligible'
    ),
    infoDetails: t(
      'privatehealth:calculator.results.otherOccupation.isOverHanseMerkurAgeThreshold.details.spouse',
      {
        defaultValue:
          'Since they’re earning above {{miniJobThreshold}}, they can’t be a dependent and need to earn at least {{hallescheOtherThreshold}} to be eligible.',
        miniJobThreshold: englishFormattedEuroCurrency(
          YEARLY_MINIJOB_THRESHOLD,
          true
        ),
        hallescheOtherThreshold: englishFormattedEuroCurrency(
          HALLESCHE_OTHER_INCOME_THRESHOLD,
          true
        ),
      }
    ),
  },

  'SELF_EMPLOYED--INCOME_TOO_LOW_BUT_TOO_OLD_FOR_HANSE_MERKUR': {
    infoTitle: t(
      'privatehealth:calculator.results.selfEmployed.isOverHanseMerkurAgeThreshold.title',
      'You might not be eligible'
    ),
    infoDetails: t(
      'privatehealth:calculator.results.selfEmployed.isOverHanseMerkurAgeThreshold.details',
      {
        defaultValue:
          'As a freelancer, you need to earn at least {{hallescheFreelancerThreshold}} to be eligible. [Talk to us]({{talkToUsLink}}) to know your options.',
        hallescheFreelancerThreshold: englishFormattedEuroCurrency(
          HALLESCHE_FREELANCER_INCOME_THRESHOLD,
          true
        ),
        talkToUsLink: calendly.healthInsuranceAdvice,
      }
    ),
  },
  'SPOUSE--SELF_EMPLOYED--INCOME_TOO_LOW_BUT_TOO_OLD_FOR_HANSE_MERKUR': {
    infoTitle: t(
      'privatehealth:calculator.results.selfEmployed.isOverHanseMerkurAgeThreshold.title.spouse',
      'They might not be eligible'
    ),
    infoDetails: t(
      'privatehealth:calculator.results.selfEmployed.isOverHanseMerkurAgeThreshold.details.spouse',
      {
        defaultValue:
          'Since they’re earning above {{miniJobThreshold}}, they can’t be a dependent and need to earn at least {{hallescheFreelancerThreshold}} to be eligible.',
        miniJobThreshold: englishFormattedEuroCurrency(
          YEARLY_MINIJOB_THRESHOLD,
          true
        ),
        hallescheFreelancerThreshold: englishFormattedEuroCurrency(
          HALLESCHE_FREELANCER_INCOME_THRESHOLD,
          true
        ),
      }
    ),
  },

  'SPOUSE--MAIN_NOT_ANSWERED': {
    infoTitle: t(
      'privatehealth:calculator.results.mainNotAnswered.spouse.title',
      'You need to fill in your own details to see the family price'
    ),
  },
  'SPOUSE--MAIN_NOT_ELIGIBLE': {
    infoTitle: t(
      'privatehealth:calculator.results.mainNotEligible.spouse.title',
      'You must be eligible on your own to see the family price'
    ),
  },
  'SPOUSE--MAIN_ON_HANSE_MERKUR': {
    infoTitle: t(
      'privatehealth:calculator.results.mainOnHanseMerkur.spouse.title',
      'We can’t estimate the price for your spouse'
    ),
    infoDetails: t(
      'privatehealth:calculator.results.mainOnHanseMerkur.spouse.details',
      'Since your own tariff might differ, we can’t estimate the price for your spouse.'
    ),
  },

  'CHILD--TOO_OLD': {
    infoTitle: t(
      'privatehealth:calculator.results.tooOld.child.title',
      'Children must be under 25 years old to be covered as dependents'
    ),
    infoDetails: t(
      'privatehealth:calculator.results.tooOld.child.details',
      'They’ll need to sign up for a policy by themselves.'
    ),
  },
  'CHILD--MAIN_NOT_ANSWERED': {
    infoTitle: t(
      'privatehealth:calculator.results.mainNotAnswered.child.title',
      'You need to fill in your own details to see the family price'
    ),
  },
  'CHILD--MAIN_NOT_ELIGIBLE': {
    infoTitle: t(
      'privatehealth:calculator.results.mainNotEligible.child.title',
      'You must be eligible on your own to see the family price'
    ),
  },
  'CHILD--MAIN_ON_HANSE_MERKUR': {
    infoTitle: t(
      'privatehealth:calculator.results.mainOnHanseMerkur.child.title',
      'We can’t estimate the price for your child'
    ),
    infoDetails: t(
      'privatehealth:calculator.results.mainOnHanseMerkur.child.details',
      'Since your own tariff might differ, we can’t estimate the price for your child.'
    ),
  },
});
